/**
 * Personal Recipe Assets Loader
 *
 * Handles cross-platform loading of personal recipe data.
 * Uses different strategies for React Native vs Web.
 */

import { Platform } from 'react-native';
import { PersonalRecipeIndex } from './personalRecipeIndex';
import { Recipe } from '../types/recipe';
import bundledRecipeIndex from '../../assets/recipes/index.json';

class PersonalRecipeAssetsLoader {
  private indexCache: PersonalRecipeIndex | null = null;
  private detailsCache = new Map<string, Recipe>();

  /**
   * Load the recipe index with platform-specific handling
   */
  async loadRecipeIndex(): Promise<PersonalRecipeIndex> {
    if (this.indexCache) {
      return this.indexCache;
    }

    try {
      let indexData: PersonalRecipeIndex;

      if (Platform.OS === 'web') {
        // For web, we'll fetch the JSON file from public assets
        console.log('PersonalRecipeAssets - Loading index via fetch for web platform');
        try {
          const response = await fetch('/assets/recipes/index.json');
          if (!response.ok) {
            throw new Error(`Failed to fetch index.json: ${response.status}`);
          }
          indexData = await response.json();
        } catch (fetchError) {
          console.warn('PersonalRecipeAssets - Failed to fetch from public assets, personal recipes not available');
          throw fetchError;
        }
      } else {
        // For React Native, disable for now to avoid bundling issues
        console.log('PersonalRecipeAssets - Personal recipes disabled for native platforms (bundling limitations)');
        throw new Error('Personal recipes not available on native platforms yet');
      }

      this.indexCache = indexData;
      console.log(`PersonalRecipeAssets - Loaded ${indexData.totalRecipes} recipes from index`);
      return indexData;

    } catch (error) {
      console.warn('PersonalRecipeAssets - Fetch failed, using bundled index:', error);

      // Use bundled static index as guaranteed fallback
      this.indexCache = bundledRecipeIndex as unknown as PersonalRecipeIndex;
      return this.indexCache;
    }
  }

  /**
   * Load individual recipe details with platform-specific handling
   */
  async loadRecipeDetails(id: string): Promise<Recipe | null> {
    if (this.detailsCache.has(id)) {
      return this.detailsCache.get(id)!;
    }

    try {
      let recipeData: Recipe;

      if (Platform.OS === 'web') {
        // For web, fetch from public assets
        try {
          const response = await fetch(`/assets/recipes/details/${id}.json`);
          if (!response.ok) {
            throw new Error(`Failed to fetch recipe ${id}: ${response.status}`);
          }
          recipeData = await response.json();
        } catch (fetchError) {
          console.warn(`PersonalRecipeAssets - Failed to fetch recipe details for ${id}`);
          throw fetchError;
        }
      } else {
        // For React Native, disable for now
        throw new Error('Personal recipe details not available on native platforms yet');
      }

      // Normalize the recipe data to ensure it matches our Recipe interface
      const recipe: Recipe = {
        ...recipeData,
        id: recipeData.id as any, // Keep original UUID string ID for personal recipes
        isPersonalRecipe: true,
        personalRecipeId: recipeData.id as any,
        // Fix image path for web serving
        image: Platform.OS === 'web' && recipeData.image
          ? `/assets/recipes/${recipeData.image}`
          : recipeData.image
      };

      this.detailsCache.set(id, recipe);
      return recipe;

    } catch (error) {
      console.warn(`PersonalRecipeAssets - Failed to load recipe ${id}:`, error);
      return null;
    }
  }

  /**
   * Check if personal recipes are available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const index = await this.loadRecipeIndex();
      return index.totalRecipes > 0;
    } catch {
      return false;
    }
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.indexCache = null;
    this.detailsCache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      indexCached: !!this.indexCache,
      detailsCacheSize: this.detailsCache.size,
      totalRecipes: this.indexCache?.totalRecipes || 0
    };
  }
}

export default new PersonalRecipeAssetsLoader();